import { useGoogleLogin } from '@react-oauth/google';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { apiPostAuthLogin, APIPostAuthLoginPayload, AuthToken } from '@/shared/api';
import { Button } from '@/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { cn } from '@/shared/utils';

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const mutation = useMutation<AxiosResponse<AuthToken>, Error, APIPostAuthLoginPayload>({
    mutationFn: (payload) => {
      return apiPostAuthLogin(payload);
    },
    onSuccess: ({ data }) => {
      localStorage.setItem('token', data.access);

      queryClient.resetQueries();
    },
  });

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {intl.formatMessage({ id: 'login.greeting' })}
          </CardTitle>
          <CardDescription>
            {intl.formatMessage({ id: 'login.description' })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              mutation.mutate({
                password,
                email,
              });
            }}
          >
            <div className="flex flex-col gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email">{intl.formatMessage({ id: 'login.email' })}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  required
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">
                    {intl.formatMessage({ id: 'login.password' })}
                  </Label>
                  {/* <a
                    href="#"
                    className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                  >
                    Forgot your password?
                  </a> */}
                </div>
                <Input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full">
                {mutation.isPending
                  ? intl.formatMessage({ id: 'login.buttonLoading' })
                  : intl.formatMessage({ id: 'login.button' })}
              </Button>

              {/* TODO: Finish and uncomment after MVP */}
              {/* <ContinueWithGoogleSection /> */}
            </div>
            {/* <div className="mt-4 text-center text-sm">
              Don&apos;t have an account?{' '}
              <a href="#" className="underline underline-offset-4">
                Sign up
              </a>
            </div> */}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ContinueWithGoogleSection = () => {
  const queryClient = useQueryClient();

  const login = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
      const res = await fetch('http://localhost:8000/api/v1/dj-rest-auth/google/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: codeResponse.code,
          redirect_uri: 'http://localhost:5173/auth/callback',
        }),
      });

      const data = await res.json();
      localStorage.setItem('token', data.access);
      queryClient.resetQueries();
    },
    scope: 'openid email profile',
    ux_mode: 'popup',
    redirect_uri: 'http://localhost:5173/auth/callback',
  });

  return (
    <>
      <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
        <span className="relative z-10 bg-background px-2 text-muted-foreground">
          Or continue with
        </span>
      </div>

      <Button variant="outline" className="w-full" onClick={login}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path
            d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
            fill="currentColor"
          />
        </svg>
        Login with Google
      </Button>
    </>
  );
};
